import React from 'react';
import { Box, Typography, TextField, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Lock as LockIcon } from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius } from '../../constants';

/**
 * Password field styles
 */
const passwordFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: borderRadius.input,
    fontSize: fonts.fontSizeSm,
    backgroundColor: 'transparent',
    '& fieldset': {
      borderColor: colors.borderDefault,
    },
    '&:hover fieldset': {
      borderColor: colors.borderHover,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.primaryDark,
      borderWidth: '2px',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '14px 16px',
    fontSize: fonts.fontSizeSm,
    color: colors.textPrimaryDark,
  },
};

/**
 * PasswordField Component
 * Reusable password field component with visibility toggle
 */
const PasswordField = ({
  label,
  value,
  onChange,
  showPassword,
  onToggleVisibility,
  placeholder,
  helperText,
  error,
  errorText,
}) => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: fonts.fontSizeSm,
          color: colors.textPrimaryDark,
          fontWeight: fonts.weightSemiBold,
          marginBottom: spacing.xs,
          letterSpacing: fonts.letterSpacingNormal,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xs,
        }}
      >
    <LockIcon sx={{ color: colors.primaryDark, fontSize: fonts.fontSizeSm }} />
        {label}
      </Typography>
      <TextField
        type={showPassword ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        fullWidth
        size="small"
        error={error}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
            <LockIcon sx={{ color: value ? colors.primaryDark : colors.textSecondary, fontSize: fonts.fontSizeSm }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={onToggleVisibility}
                edge="end"
                size="small"
                sx={{ color: colors.textSecondary }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          ...passwordFieldStyles,
          '& .MuiOutlinedInput-root': {
            ...passwordFieldStyles['& .MuiOutlinedInput-root'],
            '&.Mui-error fieldset': {
              borderColor: colors.error,
            },
          },
        }}
      />
      {error && errorText ? (
        <Typography
          sx={{
            fontSize: fonts.fontSizeXs,
            color: colors.error,
            mt: spacing.xs,
          }}
        >
          {errorText}
        </Typography>
      ) : helperText ? (
        <Typography
          sx={{
            fontSize: fonts.fontSizeXs,
            color: colors.textSecondary,
            mt: spacing.xs,
          }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
};

export default PasswordField;
